import React, { useState, useRef, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, Sparkles, Loader2, Trash2 } from "lucide-react";

export default function UploadDialog({ open, onOpenChange, onDone }) {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploads, setUploads] = useState([]);
  const inputRef = useRef(null);

  const loadUploads = () => api.get("/uploads").then((r) => setUploads(r.data)).catch(() => {});
  useEffect(() => { if (open) loadUploads(); }, [open]);

  const deleteUpload = async (u) => {
    if (!window.confirm(`Delete "${u.filename}"? This permanently removes its imported records and updates your figures.`)) return;
    try {
      const r = await api.delete(`/uploads/${u.upload_id}`);
      toast.success(`Removed "${u.filename}" (${r.data.removed} records)`);
      loadUploads();
      onDone?.();
    } catch (e) { toast.error("Could not delete this import."); }
  };

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    let ok = 0;
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await api.post("/reports/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`${data.filename}: ${data.records_ingested} records (${data.report_type})`);
        ok++;
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
      }
    }
    setBusy(false);
    if (ok) {
      onDone?.();
      onOpenChange(false);
    }
  };

  const loadSample = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/data/sample");
      toast.success(`Sample data loaded: ${data.orders} orders across 6 months`);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load sample");
    } finally {
      setBusy(false);
    }
  };

  const resetData = async () => {
    setBusy(true);
    try {
      await api.delete("/data/reset");
      toast.success("All data cleared");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error("Failed to reset");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0d0d0d] border-[#262626] text-white max-w-lg" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-xl">Import Amazon Data</DialogTitle>
          <DialogDescription className="text-[#A3A3A3]">
            Upload a report export (CSV / TSV / XLSX). Auto-detects All Orders, FBA Inventory & Inbound Shipments.
          </DialogDescription>
        </DialogHeader>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
          data-testid="upload-dropzone"
          className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors duration-200 ${
            dragging ? "border-[#007AFF] bg-[#007AFF]/10" : "border-[#333] hover:border-[#555] bg-[#111]"
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".csv,.tsv,.txt,.xlsx,.xls"
            className="hidden"
            data-testid="file-input"
            onChange={(e) => handleFiles(e.target.files)}
          />
          {busy ? (
            <Loader2 className="h-8 w-8 mx-auto text-[#007AFF] animate-spin" />
          ) : (
            <UploadCloud className="h-8 w-8 mx-auto text-[#007AFF]" />
          )}
          <p className="mt-3 text-sm font-semibold">Drop files or click to browse</p>
          <p className="text-xs text-[#666] mt-1">CSV, TSV, XLSX up to 20MB</p>
        </div>

        <div className="flex items-center gap-2 text-xs text-[#666]">
          <FileSpreadsheet className="h-3.5 w-3.5" /> No account access needed — just export & upload.
        </div>

        <div className="border-t border-[#262626] pt-4 flex flex-col gap-2">
          <p className="text-xs uppercase tracking-widest text-[#666]">No file handy?</p>
          <Button
            onClick={loadSample}
            disabled={busy}
            data-testid="load-sample-button"
            className="w-full bg-[#10B981] hover:bg-[#0d9668] text-black font-semibold rounded-full transition-colors duration-200"
          >
            <Sparkles className="h-4 w-4 mr-2" /> Load 6-month sample dataset
          </Button>
          <Button
            onClick={resetData}
            disabled={busy}
            variant="ghost"
            data-testid="reset-data-button"
            className="w-full text-[#A3A3A3] hover:text-[#EF4444] hover:bg-[#EF4444]/10"
          >
            <Trash2 className="h-4 w-4 mr-2" /> Clear all data
          </Button>
        </div>

        {uploads.length > 0 && (
          <div className="border-t border-[#262626] pt-4" data-testid="uploads-history">
            <p className="text-xs uppercase tracking-widest text-[#666] mb-2">Imported data</p>
            <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
              {uploads.map((u) => (
                <div key={u.upload_id} data-testid={`upload-row-${u.upload_id}`} className="flex items-center justify-between gap-2 rounded-lg bg-[#111] border border-[#222] px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">{u.filename}</p>
                    <p className="text-xs text-[#666]">{u.report_type} · {u.records} records · {(u.uploaded_at || "").slice(0, 10)}</p>
                  </div>
                  <button
                    data-testid={`delete-upload-${u.upload_id}`}
                    onClick={() => deleteUpload(u)}
                    disabled={busy}
                    className="shrink-0 text-[#666] hover:text-[#EF4444] transition-colors duration-150"
                    title="Delete this import"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
